import { FC, useCallback, useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { getAddressSearch, getMarketListByAddressId } from "tm-shared/api";
import { validationMessage } from "tm-shared/constants";
import { Market, Option } from "tm-shared/types";

import { RHFAutocomplete } from "@/components/RHF/RHFAutocomplete";
import { useAppDispatch } from "@/hooks/redux";
import { useAsyncOptions } from "@/hooks/useAsyncOptions";
import { AddressVerifyFormValues } from "@/types/form";

interface AddressSectionProps {
    regionId: string;
    address: string;
    isChecking: boolean;
    setIsChecking: (v: boolean) => void;
    options: Option[];
    setOptions: (o: Option[]) => void;
    onOpenAddAddressModal: () => void;
    onOpenDuplicateModal: (args: {
        markets: Market[];
        addressLabel: string;
    }) => void;
}

export const AddressSection: FC<AddressSectionProps> = ({
    onOpenAddAddressModal,
    onOpenDuplicateModal,
    regionId,
    address,
    isChecking,
    options,
    setOptions,
    setIsChecking,
}) => {
    const dispatch = useAppDispatch();

    const { setValue, setError } = useFormContext<AddressVerifyFormValues>();

    const handleOnAfterChangeAddress = useCallback(() => {
        setValue("parentId", "");
    }, [setValue]);

    const [searchQuery, setSearchQuery] = useState("");

    const { options: fetchedOptions, loading: loadingAddress } =
        useAsyncOptions({
            fetchFn: (signal) => {
                return getAddressSearch(
                    {
                        name: searchQuery,
                        areaId: regionId,
                    },
                    {
                        axiosOverrides: { signal },
                    },
                );
            },
            selectData: (response) => Object.entries(response.result),
            mapToOption: ([value, label]) => ({ label, value }),
            dependencies: [searchQuery],
            enabled: (!searchQuery && !address) || searchQuery.length >= 3,
            debounceMs: 500,
        });

    useEffect(() => {
        if (fetchedOptions?.length) {
            setOptions(fetchedOptions);
        }
    }, [dispatch, fetchedOptions, setOptions]);

    const handleInputChange = useCallback(
        (value: string) => {
            setSearchQuery(value);
            setValue("address", "");
        },
        [setValue],
    );

    const checkDuplicateMarkets = useCallback(
        async (address: string) => {
            if (!address) return false;

            const currentOption = options.find((o) => o.value === address);

            setIsChecking(true);
            try {
                const { result: marketsFromServer } =
                    await getMarketListByAddressId({
                        addressId: address,
                    });

                if (marketsFromServer.length > 0) {
                    onOpenDuplicateModal({
                        markets: marketsFromServer,
                        addressLabel: currentOption?.label ?? "",
                    });
                }
            } catch (error) {
                setError("address", {
                    message: "Помилка при перевірці адреси",
                });

                return "Помилка при перевірці адреси";
            } finally {
                setIsChecking(false);
            }
        },
        [options, setIsChecking, onOpenDuplicateModal, setError],
    );

    useEffect(() => {
        if (address) void checkDuplicateMarkets(address);
    }, [checkDuplicateMarkets, options, address]);

    return (
        <RHFAutocomplete
            name="address"
            options={options ?? []}
            label="Адреса"
            rules={{
                required: validationMessage.required,
            }}
            actionOption={{
                label: "Додати адресу",
                onClick: onOpenAddAddressModal,
            }}
            allowClear={false}
            disabled={isChecking}
            loading={loadingAddress}
            onInputChange={handleInputChange}
            selectProps={{
                size: "large",
            }}
            onAfterChange={handleOnAfterChangeAddress}
        />
    );
};
